"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.examSessionListQuerySchema = exports.enterResultsSchema = exports.markAttendanceSchema = exports.addToRosterSchema = exports.ROSTER_ADD_MAX = exports.examSessionStepSchema = exports.cancelExamSessionSchema = exports.rescheduleExamSessionSchema = exports.updateExamSessionSchema = exports.createExamSessionSchema = exports.examSessionInputSchema = exports.NO_SHOW_CHANCES_MAX = exports.DEFAULT_NO_SHOW_CHANCES = exports.DAY_BEFORE_HOURS = exports.ROSTER_LOCK_HOURS = exports.EXAM_CAPACITY_MAX = exports.EXAM_NOTE_MAX = exports.EXAM_VENUE_MAX = exports.EXAM_NAME_MAX = exports.MISSED_ATTENDANCE = exports.MARKABLE_ATTENDANCE = exports.UPCOMING_SESSION_STATUSES = exports.ATTENDANCE_STATUS_LABELS = exports.EXAM_SESSION_STATUS_LABELS = void 0;
exports.isShortNotice = isShortNotice;
exports.isAutoLockDue = isAutoLockDue;
exports.isDayBeforeDue = isDayBeforeDue;
exports.sessionEndsAt = sessionEndsAt;
exports.rosterProblem = rosterProblem;
exports.missingForSchedule = missingForSchedule;
exports.examOutcome = examOutcome;
exports.scoreProblem = scoreProblem;
exports.mayBookAgain = mayBookAgain;
exports.examMessageText = examMessageText;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// S18 — on-site exam sessions (ASM-04, ASM-05). Sources: master plan S18 and its planning
// decisions (DECISIONS-LOG S18 Q1–Q9); F9.08–F9.14; E7; §L6; §L7; §L19 ASM-NOSHOW;
// §L22 N-26 and N-74–N-76; §L23; §L25 exam session and attendance; ADR 0020.
exports.EXAM_SESSION_STATUS_LABELS = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    roster_locked: 'List locked',
    in_progress: 'Exam running',
    results_entry: 'Entering results',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rescheduled: 'Moved',
};
/** E7: a no-show is its own status, never a fail — "No-show", not "Absent / failed". */
exports.ATTENDANCE_STATUS_LABELS = {
    registered: 'Booked',
    present: 'Present',
    completed: 'Result in',
    absent: 'No-show',
    rescheduled: 'Asked to move',
};
/** Statuses in which a session still happens: people can be booked, it can be moved or cancelled. */
exports.UPCOMING_SESSION_STATUSES = ['draft', 'scheduled', 'roster_locked'];
/** What the invigilator can mark a person as on the day (F9.11). */
exports.MARKABLE_ATTENDANCE = ['present', 'absent', 'rescheduled'];
/** Did not sit the exam on the day — counts against the job's second chances (S18 Q5). */
exports.MISSED_ATTENDANCE = ['absent', 'rescheduled'];
exports.EXAM_NAME_MAX = 120;
exports.EXAM_VENUE_MAX = 200;
exports.EXAM_NOTE_MAX = 1000;
exports.EXAM_CAPACITY_MAX = 500;
/** §L25 / F9.10: the list locks this long before the start. */
exports.ROSTER_LOCK_HOURS = 24;
/** §L22 N-26: "exam session tomorrow". */
exports.DAY_BEFORE_HOURS = 24;
/** A job's "second chances after a no-show" (S18 Q5): the E7 default, and a sane ceiling. */
exports.DEFAULT_NO_SHOW_CHANCES = 1;
exports.NO_SHOW_CHANCES_MAX = 5;
const HOUR_MS = 3_600_000;
const nullableText = (max) => zod_1.z.string().trim().max(max).nullable();
/**
 * F9.08. One job per session (S18 Q3); the test is the job's own (S18 Q1). A session
 * with a start, a venue and an invigilator is scheduled at once; without them it
 * stays a draft (§L25 draft → scheduled).
 */
exports.examSessionInputSchema = zod_1.z.object({
    name: nullableText(exports.EXAM_NAME_MAX),
    venue: nullableText(exports.EXAM_VENUE_MAX),
    startsAt: zod_1.z.iso.datetime({ message: 'Pick a date and time.' }).nullable(),
    capacity: zod_1.z.number().int('Whole numbers only.').min(1, 'At least 1 seat.').max(exports.EXAM_CAPACITY_MAX),
    invigilatorUserId: schemas_1.uuidSchema.nullable(),
    note: nullableText(exports.EXAM_NOTE_MAX),
});
exports.createExamSessionSchema = zod_1.z.object({ jobId: schemas_1.uuidSchema, session: exports.examSessionInputSchema });
/** Edits the details. Changing the start of a scheduled session is a move, which tells everyone. */
exports.updateExamSessionSchema = schemas_1.versionedSchema.extend({ session: exports.examSessionInputSchema });
/** F9.14 — move: a new start, and the venue if it changes too. Candidates are told (N-75). */
exports.rescheduleExamSessionSchema = schemas_1.versionedSchema.extend({
    startsAt: zod_1.z.iso.datetime({ message: 'Pick a date and time.' }),
    venue: zod_1.z.string().trim().min(1, 'This field is required.').max(exports.EXAM_VENUE_MAX),
});
/** F9.14 — cancel. A reason, always (§L25 marks it `requiresReason`). */
exports.cancelExamSessionSchema = schemas_1.versionedSchema.extend({
    reason: zod_1.z.string().trim().min(1, 'Say why the exam is cancelled.').max(exports.EXAM_NOTE_MAX),
});
/** A plain state change of the whole session: lock, start, finish, complete. */
exports.examSessionStepSchema = schemas_1.versionedSchema;
exports.ROSTER_ADD_MAX = 200;
/** F9.09 from the session page, and one row's "Book a session" from the results. */
exports.addToRosterSchema = zod_1.z.object({
    applicationIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Choose at least one person.').max(exports.ROSTER_ADD_MAX),
});
/** F9.11 — one or many rows at once ("Mark everyone present"). */
exports.markAttendanceSchema = zod_1.z.object({
    marks: zod_1.z
        .array(zod_1.z.object({ attendanceId: schemas_1.uuidSchema, status: zod_1.z.enum(exports.MARKABLE_ATTENDANCE) }))
        .min(1)
        .max(exports.EXAM_CAPACITY_MAX),
});
/** Marks to two decimal places, as the rest of the scores are stored. */
const scoreSchema = schemas_1.twoDecimalMarkSchema;
/** F9.12 — one total per person (S18 Q1), one row or the whole list. Typing again corrects it. */
exports.enterResultsSchema = zod_1.z.object({
    results: zod_1.z
        .array(zod_1.z.object({ attendanceId: schemas_1.uuidSchema, score: scoreSchema }))
        .min(1)
        .max(exports.EXAM_CAPACITY_MAX),
});
// --- Rules -------------------------------------------------------------------------------
/**
 * A session set less than a day ahead (S18 Q9). Its list is never locked by the system:
 * it locks on "Lock list" or when the exam starts, and the booking email does the day
 * before's job.
 */
function isShortNotice(startsAt, scheduledAt) {
    return startsAt.getTime() - scheduledAt.getTime() < exports.ROSTER_LOCK_HOURS * HOUR_MS;
}
/** F9.10: the sweep locks a scheduled session's list at T-24h — unless it was set at short notice. */
function isAutoLockDue(input) {
    if (input.status !== 'scheduled' || input.isShortNotice || !input.startsAt)
        return false;
    return input.now.getTime() >= input.startsAt.getTime() - exports.ROSTER_LOCK_HOURS * HOUR_MS;
}
/** N-26 goes once, from T-24h until the start; a session that has begun is past reminding. */
function isDayBeforeDue(input) {
    if (input.dayBeforeSentAt || !input.startsAt)
        return false;
    if (input.status !== 'scheduled' && input.status !== 'roster_locked')
        return false;
    const now = input.now.getTime();
    return now >= input.startsAt.getTime() - exports.DAY_BEFORE_HOURS * HOUR_MS && now < input.startsAt.getTime();
}
/** The session's end, from the test's length — the paper is the test (S18 Q1). */
function sessionEndsAt(startsAt, durationMinutes) {
    return new Date(startsAt.getTime() + Math.max(durationMinutes, 1) * 60_000);
}
/** What stops someone going on this session's list, in the words shown beside their name. */
function rosterProblem(input) {
    if (input.status !== 'scheduled') {
        return input.status === 'roster_locked' ? 'The list is locked. Book them on another session.' : 'People can only be added to a scheduled session.';
    }
    if (input.booked >= input.capacity)
        return `The session is full (${input.capacity} of ${input.capacity} seats).`;
    return null;
}
/** A session may be scheduled once it has everything §L25 asks for. */
function missingForSchedule(input) {
    const missing = [];
    if (!input.startsAt)
        missing.push('a date and time');
    if (!input.venue)
        missing.push('a venue');
    if (!input.invigilatorUserId)
        missing.push('an invigilator');
    return missing;
}
/** One total out of the paper's marks (S18 Q1), judged on the raw figures like an online test. */
function examOutcome(score, maxScore, passPercent) {
    const scorePercent = maxScore === 0 ? 0 : Math.round((score / maxScore) * 10_000) / 100;
    return { scorePercent, didPass: score * 100 >= passPercent * maxScore };
}
function scoreProblem(score, maxScore) {
    if (score < 0)
        return 'A mark cannot be below 0.';
    if (score > maxScore)
        return `The paper is out of ${maxScore}.`;
    return null;
}
/**
 * Whether someone who missed a session may be booked again (S18 Q5): each day missed —
 * a no-show, or asking on the day to come another time — uses one second chance. A
 * session the company cancelled or moved uses none.
 */
function mayBookAgain(missedSessions, noShowChances) {
    return missedSessions <= noShowChances;
}
exports.examSessionListQuerySchema = zod_1.z.object({
    jobId: schemas_1.uuidSchema.optional(),
    /** `upcoming` (default): draft, scheduled, locked, running, results; `past`: completed and cancelled. */
    when: zod_1.z.enum(['upcoming', 'past']).default('upcoming'),
});
/** The per-candidate message "Copy message" puts on the clipboard (S18 Q4), matching the email. */
function examMessageText(input) {
    const greeting = `Dear ${input.candidateName},`;
    const sign = `${input.companyName} recruitment team`;
    if (input.status === 'cancelled') {
        return [greeting, '', `Your written exam for ${input.jobTitle} at ${input.companyName}, on ${input.when}, is cancelled. We will be in touch about a new date.`, '', sign].join('\n');
    }
    return [
        greeting,
        '',
        `You are booked for the written exam for ${input.jobTitle} at ${input.companyName}.`,
        `When: ${input.when}`,
        `Where: ${input.venue}`,
        '',
        'If you cannot come, reply to let us know.',
        '',
        sign,
    ].join('\n');
}
//# sourceMappingURL=exam-sessions.js.map